// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	type ResourceStoreContext,
	type RouteResource,
} from '@atlassian/react-resource-router';

export type AssetsPrimaryWorkspace = {
	readonly cmdbSiteCloudId: string | null;
	readonly workspaceId: string | null;
};

export const RESOURCE_TYPE_ASSETS_PRIMARY_WORKSPACE = 'JSM_ASSETS_PRIMARY_WORKSPACE';

const ASSETS_INTERNAL_WORKSPACE_RESOLVER_URL = '/assets/internal/workspace?productContext=jsm';

type WorkspaceLatestResponse = {
	readonly workspaceId?: string | null;
	readonly site?: string | null;
	readonly cmdbSiteCloudId?: string | null;
};

const EMPTY_RESULT: AssetsPrimaryWorkspace = Object.freeze({
	cmdbSiteCloudId: null,
	workspaceId: null,
});

const ERROR_META = {
	id: 'assetsPrimaryWorkspaceResource',
	packageName: 'jiraRouterResourcesAssetsPrimaryWorkspace',
	teamName: 'rohirrim',
} as const;

export const fetchAssetsPrimaryWorkspace = async (
	cloudId: string | undefined,
): Promise<AssetsPrimaryWorkspace> => {
	if (cloudId == null || cloudId === '') {
		return EMPTY_RESULT;
	}
	try {
		const response = await performGetRequest<WorkspaceLatestResponse>(
			ASSETS_INTERNAL_WORKSPACE_RESOLVER_URL,
			{
				headers: {
					accept: 'application/json',
					'x-atlassian-cloud-id': cloudId,
				},
			},
		);

		if (!response?.cmdbSiteCloudId) {
			return EMPTY_RESULT;
		}

		return Object.freeze({
			cmdbSiteCloudId: response.cmdbSiteCloudId,
			workspaceId: response.workspaceId ?? null,
		});
	} catch (error: unknown) {
		fireErrorAnalytics({
			meta: ERROR_META,
			error: error instanceof Error ? error : new Error(String(error)),
			attributes: { reason: 'resolverUnavailableOrError' },
			sendToPrivacyUnsafeSplunk: true,
		});
		return EMPTY_RESULT;
	}
};

export const assetsPrimaryWorkspaceResource: RouteResource<AssetsPrimaryWorkspace> =
	createResource<AssetsPrimaryWorkspace>({
		type: RESOURCE_TYPE_ASSETS_PRIMARY_WORKSPACE,
		getKey: (_, { tenantContext }: ResourceStoreContext) => tenantContext?.cloudId ?? 'no-cloud-id',
		getData: async (_, { tenantContext }: ResourceStoreContext) =>
			fetchAssetsPrimaryWorkspace(tenantContext?.cloudId),
		maxAge: Infinity,
	});
